import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Truck, Store, Zap, ChevronRight } from "lucide-react";
import api, { gbp } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PAYMENTS = [["card", "Credit / Debit Card"], ["paypal", "PayPal"], ["klarna", "Klarna"], ["clearpay", "Clearpay"], ["applepay", "Apple Pay"], ["googlepay", "Google Pay"]];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
    <input {...props} className="w-full border border-border bg-secondary/60 px-3 py-3 text-sm outline-none focus:border-accent transition-colors" />
  </label>
);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("standard");
  const [payment, setPayment] = useState("card");
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({ full_name: user?.name || "", email: user?.email || "", address: "", city: "", postcode: "", phone: "" });

  const delivery = method === "nextday" ? 6.99 : method === "collect" ? 0 : subtotal >= 50 ? 0 : 3.99;
  const total = subtotal + delivery;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (items.length === 0)
    return <div className="container-furn py-24 text-center"><h1 className="font-display text-3xl">Your basket is empty</h1><Link to="/shop" className="mt-4 inline-block text-accent">Back to shop</Link></div>;

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || (method !== "collect" && (!form.address || !form.postcode))) {
      toast.error("Please complete the required fields.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        shipping: { ...form, method },
        payment_method: payment,
      });
      clear();
      navigate("/order-confirmation", { state: { order: data } });
    } catch {
      toast.error("Something went wrong placing your order.");
    } finally { setPlacing(false); }
  };

  return (
    <div className="container-furn py-12">
      <nav className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground"><Link to="/cart" className="hover:text-accent">Basket</Link><ChevronRight className="h-3 w-3" /><span className="text-foreground">Checkout</span></nav>
      <h1 className="mb-8 font-display text-4xl font-bold uppercase tracking-tight">Checkout</h1>

      <form onSubmit={placeOrder} className="grid gap-10 lg:grid-cols-[1fr_380px]">
        <div className="space-y-10">
          {!user && (
            <div className="border border-border bg-card p-4 text-sm">
              Checking out as guest. <Link to="/login" className="font-semibold text-accent">Sign in</Link> to earn loyalty points & track orders.
            </div>
          )}

          <section>
            <h2 className="mb-4 font-display text-xl font-semibold uppercase">Delivery Method</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {[["standard", Truck, "Standard", subtotal >= 50 ? "FREE" : "£3.99", "2–4 days"], ["nextday", Zap, "Next Day", "£6.99", "Order by 2pm"], ["collect", Store, "Click & Collect", "FREE", "SWF store"]].map(([val, Icon, label, price, sub]) => (
                <button type="button" key={val} onClick={() => setMethod(val)} data-testid={`delivery-${val}`} className={`flex flex-col items-start gap-1 border p-4 text-left transition-colors ${method === val ? "border-accent bg-accent/5" : "border-border hover:border-accent/50"}`}>
                  <Icon className="h-5 w-5 text-accent" />
                  <span className="mt-1 text-sm font-semibold">{label}</span>
                  <span className="text-xs text-muted-foreground">{sub}</span>
                  <span className="text-sm font-bold text-accent">{price}</span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4 font-display text-xl font-semibold uppercase">{method === "collect" ? "Your Details" : "Delivery Address"}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name *" value={form.full_name} onChange={set("full_name")} data-testid="checkout-name" />
              <Field label="Email *" type="email" value={form.email} onChange={set("email")} data-testid="checkout-email" />
              {method !== "collect" && <>
                <Field label="Address *" value={form.address} onChange={set("address")} data-testid="checkout-address" />
                <Field label="City" value={form.city} onChange={set("city")} data-testid="checkout-city" />
                <Field label="Postcode *" value={form.postcode} onChange={set("postcode")} data-testid="checkout-postcode" />
              </>}
              <Field label="Phone" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" />
            </div>
          </section>

          <section>
            <h2 className="mb-4 font-display text-xl font-semibold uppercase">Payment</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {PAYMENTS.map(([val, label]) => (
                <button type="button" key={val} onClick={() => setPayment(val)} data-testid={`payment-${val}`} className={`border px-4 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors ${payment === val ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent/50"}`}>{label}</button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Demo checkout — no real payment is taken.</p>
          </section>
        </div>

        <aside className="h-fit border border-border bg-card p-6 lg:sticky lg:top-32">
          <h3 className="mb-4 font-display text-xl font-semibold uppercase">Your Order</h3>
          <div className="mb-4 max-h-56 space-y-3 overflow-y-auto no-scrollbar">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3">
                <div className="h-14 w-14 shrink-0 overflow-hidden bg-secondary"><img src={i.image} alt="" className="h-full w-full object-cover" /></div>
                <div className="flex-1 text-sm"><p className="line-clamp-1 font-medium">{i.name}</p><p className="text-muted-foreground">Qty {i.quantity}</p></div>
                <span className="text-sm font-semibold">{gbp(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{delivery === 0 ? "FREE" : gbp(delivery)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 font-display text-lg font-bold"><span>Total</span><span>{gbp(total)}</span></div>
          </div>
          <button type="submit" disabled={placing} data-testid="place-order-btn" className="mt-5 flex w-full items-center justify-center gap-2 bg-accent py-4 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter] disabled:opacity-50">
            <Lock className="h-4 w-4" /> {placing ? "Placing Order…" : `Pay ${gbp(total)}`}
          </button>
        </aside>
      </form>
    </div>
  );
}
